import { useLazyQuery } from '@apollo/client';
import { createLabelWithUsername } from '../../../../../utility/userUtils';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  defaultToolbarOptions,
  solutionXPLRTooltipText,
  VENDOR_LIST,
} from '../../../../../appConstants';
import ConfirmationModal from '../../../../../common/components/ConfirmationModal';
import RichTextEditor from '../../../../../common/components/RichTextEditor/RichTextEditor';
import { IOrganization } from '../../../../../common/types/organisation';
import { debouncedUpdateField } from '../../../../../common/utils';
import {
  GET_SUBPROCESS_DATA,
  GET_TAXONOMY_DATA_LIST,
} from '../../../../../graphql/queries/getTaxonomyData';
import { TaxonomyFunctionItem } from '../../../../../slices/allTaxonomy/taxonomyTypes';
import { useGraphQLQuery } from '../../../../../customHooks/useGraphQL';
import { RootState } from '../../../../../store';
import { fetchDiagramData } from '../../../../../services/diagramService';
import { updateUserPreferences } from '../../../../../slices/userSlice';
import ShowToast from '../../../../../common/components/ShowToast';
import { ToastTypeEnum } from '../../../../../constants/solutionXPLR';
import { httpService } from '../../../../../utility/httpService';
import { httpConstants } from '../../../../../constants';
import {
  ENVIRONMENT_TYPE,
  // GENERATE_PROCESS_API_KEY,
  STORAGE_BASE,
} from '../../../../../config/env';
import storageService from '../../../../../services/storageService';
import { KeyInformationData } from '../../../shared/types/solution-xplr.interface';
import { getEnterpriseTechnology } from '../../../../../services/commonService';
// import EvaluationReportPanel from './EvaluationReportPanel';
import { uploadFilesToNexus } from '../../../service';
import ImportEditPage from '../../OpportunityReport/components/ImportEditPage';
import { AddCircleOutline, ArrowBackRounded, RemoveCircleOutline } from '@mui/icons-material';
import { ORCHESTRATOR_CONFIG } from '../../../../../config/orchestratorConfig';
import RightChatDrawer from '../../../../shared/components/RightChatDrawer';
import { getLatestQueryHistory, getIdeationIntakes } from '../../../../DataXPLR/IdeationService';
import {
  addLinkedSessions,
  clearLinkedSessions,
  // removeLinkedSession,
  // clearLinkedSessions,
} from '../../../../../slices/solutionXPLRSlice';
import SolutionMultilineField from './SolutionMultilineField';
import { LinkedSession } from '../../../shared/types/solution-xplr.interface';
import {
  INTAKE_TYPE_TO_FIELD_KEY,
  buildLinkedSessionsByField,
} from '../../../shared/utils/linkedSessionUtils';
import { deleteIdeationIntake } from '../../../../IdeationCOI/IdeationService';
import { EvaluateObject } from '../types';
import EvaluationReportV2 from '../../../../NewIdeationXPLR/modules/Opportunity/components/EvaluationReportV2';

type SubProcess = {
  id: string;
  name: string;
  diagram: object | null;
};

type Process = {
  id: string;
  name: string;
  subprocesses?: SubProcess[];
};

type E2E = {
  id: string;
  name: string;
  processes?: Process[];
};

type FunctionItem = {
  id: string;
  name: string;
  e2es?: E2E[];
};

interface GeneratedResponseType {
  isValid?: boolean;
  current_process?: string;
  [key: string]: any;
}

interface FileViewerProps {
  fileUrl: string;
  onClose: () => void;
  fileName?: string;
}

// Flatten into objects, not strings
const flattenFunctionTree = (func: any) => {
  const list: any[] = [];
  func.e2es?.forEach((e2e: any) => {
    e2e.processes?.forEach((proc: any) => {
      proc.subprocesses?.forEach((sub: any) => {
        list.push({
          label: createLabelWithUsername(`${func.id}`),
          functionId: func.id,
          e2eId: e2e.id,
          processId: proc.id,
          subprocessId: sub.id,
          functionName: func.name,
          e2eName: e2e.name,
          processName: proc.name,
          subprocessName: sub.name,
          diagram: sub.diagram,
          fullObject: sub,
        });
      });
    });
  });
  return list;
};

// Styled components
const StyledContainer = styled(Container)(() => ({
  minWidth: '100%',
  margin: '0 auto',
  padding: '0px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(0.5), // ← ADD THIS
  // paddingRight: theme.spacing(2), // ← ADD THIS
}));

// Styled component for the expandable field container
const ExpandableFieldContainer = styled(Box)(() => ({
  position: 'relative',
  '&:hover .expand-button': {
    opacity: 1,
  },
}));

// Styled component for the expand button
const ExpandButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  right: 14,
  zIndex: 10,
  opacity: 0.7,
  transition: 'opacity 0.2s ease-in-out',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  width: 28,
  height: 28,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: theme.shadows[2],
  },
}));

// interface EvaluateObject {
//   loading: boolean;
//   data: string;
//   error: Error | string | null;
//   isOpen: boolean;
// }

// Interface for props
interface KeyInformationComponentProps {
  keyInformationData: KeyInformationData;
  setKeyInformationData: React.Dispatch<React.SetStateAction<KeyInformationData>>;
  handleDraftSave: (data: KeyInformationData) => void;
  evaluateObject: EvaluateObject;
  handleCloseEvaluate: () => void;
  // Add unique key prop to force re-render when switching drafts
  draftKey?: string;
  latestKeyInfoRef: MutableRefObject<KeyInformationData | undefined>;
  versionId?: string;
  isVersionViewMode?: boolean;
  // Linked ideation intake from the previewed version's custom_metadata.
  versionLinkedIntake?: any[];
  solutionId?: string;
  selectedThreadId?: string;
  setSelectedThreadId?: (threadId: string) => void;
  evaluateSuccess?: boolean;
}

const KeyInformation: React.FC<KeyInformationComponentProps> = ({
  keyInformationData,
  setKeyInformationData,
  handleDraftSave,
  evaluateObject,
  handleCloseEvaluate,
  draftKey = '',
  latestKeyInfoRef,
  versionId = '',
  isVersionViewMode = false,
  versionLinkedIntake,
  solutionId = '',
  selectedThreadId,
  setSelectedThreadId,
  evaluateSuccess = false,
}) => {
  const editorOptions = {
    ...defaultToolbarOptions,
    image: false,
    codeBlock: false,
    textColor: false,
    highlight: false,
    link: false,
    fontFamily: false,
    table: false,
  };

  // Expand state management
  const user = useSelector((state: RootState) => state.user);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [fontSizeAnchorEl, setFontSizeAnchorEl] = useState<null | HTMLElement>(null);
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>(
    user?.preferences?.fontSize ?? 'medium'
  );
  const fontSizeMenuOpen = Boolean(fontSizeAnchorEl);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Enterprise Technology API state
  const [enterpriseTechnologies, setEnterpriseTechnologies] = useState<string[]>([]);
  const [loadingTechnologies, setLoadingTechnologies] = useState<boolean>(false);
  const [isImportBpmnOpen, setIsImportBpmnOpen] = useState(false);
  const [isBpmnImported, setIsBpmnImported] = useState(false);
  const [showImportEditPage, setShowImportEditPage] = useState(false);
  const [fetchSubprocessData] = useLazyQuery(GET_SUBPROCESS_DATA);
  const [diagramData, setDiagramData] = useState<any>(null);
  const [importType, setImportType] = useState<'import' | 'upload'>('import');
  const [additionalContent, setAdditionalContent] = useState<string>('');
  const [generatedResponse, setGeneratedResponse] = useState<GeneratedResponseType>({});
  const didAutoSave = useRef<boolean>(false);
  const [isGenerateLoading, setIsGenerateLoading] = useState<boolean>(false);
  const generatedResponseRef = useRef<GeneratedResponseType | null>(null);

  // Debouncing refs
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const userInteractionRef = useRef<boolean>(false);
  const DEBOUNCE_DELAY = 500; // 500ms delay
  const keyInformationRef = useRef<KeyInformationData>(keyInformationData);

  const selectedOrgId = localStorage.getItem('current_organization');
  const organizationsList = useSelector((state: RootState) => state.organisation) || [];
  const solutionData = useSelector((state: RootState) => state.solutionXPLR);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedOrg: IOrganization =
    organizationsList.find((org: IOrganization) => org.id === selectedOrgId) ||
    ({} as IOrganization);

  const hasOrganization = selectedOrg && selectedOrg.id && selectedOrgId;

  const taxonomyValue =
    hasOrganization &&
    Array.isArray(selectedOrg?.taxonomy?.value) &&
    selectedOrg.taxonomy.value.length > 0
      ? selectedOrg.taxonomy.value
      : [];

  const { loading: taxonomyLoading, data: taxonomyData } = useGraphQLQuery<{
    taxonomyFunction: { fetch: TaxonomyFunctionItem[] };
  }>(
    GET_TAXONOMY_DATA_LIST,
    {
      taxonomyFunctionFilters: {
        id_In: taxonomyValue,
      },
      taxonomyFunctionSort: ['name'],
      e2eFilters: {
        id_In: taxonomyValue,
      },
      e2eSort: ['name'],
      processFilters: {
        id_In: taxonomyValue,
      },
      processSort: ['name'],
      subprocessFilters: {
        id_In: taxonomyValue,
      },
      subprocessSort: ['name'],
    },
    {
      fetchPolicy: 'network-only',
    }
  );
  const rawFunctions: FunctionItem[] = taxonomyData?.taxonomyFunction?.fetch ?? [];

  const functions = Object.values(
    rawFunctions.reduce(
      (acc, fn) => {
        const key = fn.name.trim();

        if (!acc[key]) {
          acc[key] = { ...fn, e2es: [...(fn.e2es || [])] };
        } else {
          // merge e2es
          const existingE2Es = acc[key].e2es || [];
          const newE2Es = fn.e2es || [];

          const mergedE2Es = [
            ...new Map([...existingE2Es, ...newE2Es].map((e2e) => [e2e.id, e2e])).values(),
          ];

          acc[key] = { ...acc[key], e2es: mergedE2Es };
        }

        return acc;
      },
      {} as Record<string, FunctionItem>
    )
  );

  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [importConfirm, setImportConfirm] = useState<boolean>(false);
  const [selectedBpmnObj, setSelectedBpmnObj] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreview, setFilePreview] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // const [currentFieldLabel, setCurrentFieldLabel] = useState<string>('');
  const [sessions, setSessions] = useState<any[]>([]);
  const [category, setCategory] = useState<string>('Opportunity Statement');
  const [drawerOriginField, setDrawerOriginField] = useState<string | null>(null);
  console.log(category, 'selected category');

  const [searchQuery, setSearchQuery] = useState<string>('');
  const useCaseId = solutionId; // or whatever ID you already have
  const organizationId = localStorage.getItem('current_organization') || '';

  // const dataInSolutionXPLR = true;

  const selectedFunction = useMemo(
    () => functions.find((fn) => fn.id === selectedFunctionId),
    [functions, selectedFunctionId]
  );

  const flattenedList = useMemo(
    () => (selectedFunction ? flattenFunctionTree(selectedFunction) : []),
    [selectedFunction]
  );

  const filteredList = useMemo(
    () =>
      flattenedList.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [flattenedList, searchTerm]
  );

  useEffect(() => {
    if (!selectedFunctionId && functions.length > 0) {
      setSelectedFunctionId(functions[0].id);
    }
  }, [functions]);

  const handleSelectBpmn = (item: any) => {
    setSelectedBpmnObj(item);
  };
  const handleFontSizeClick = (event: React.MouseEvent<HTMLElement>) => {
    setFontSizeAnchorEl(event.currentTarget);
  };

  const handleFontSizeClose = () => {
    setFontSizeAnchorEl(null);
  };

  const handleFontSizeSelect = (size: 'small' | 'medium' | 'large') => {
    setFontSize(size);
    dispatch(updateUserPreferences({ fontSize: size }));
    setFontSizeAnchorEl(null);
  };
  // Add ref to track current draft key
  const currentDraftKeyRef = useRef<string>(draftKey);

  const uniqueKey = useMemo(() => {
    return versionId ? `${draftKey}-${versionId}` : draftKey;
  }, [draftKey, versionId]);

  // Reset refs when draft changes
  useEffect(() => {
    if (currentDraftKeyRef.current !== draftKey) {
      // Clear all pending timeouts
      Object.values(debounceTimeouts.current).forEach(clearTimeout);
      debounceTimeouts.current = {};

      // Update refs
      currentDraftKeyRef.current = draftKey;
      keyInformationRef.current = keyInformationData;
    }
  }, [draftKey, keyInformationData]);

  useEffect(() => {
    keyInformationRef.current = keyInformationData;
  }, [keyInformationData]);

  useEffect(() => {
    setFontSize('medium');
    dispatch(updateUserPreferences({ fontSize: 'medium' }));
    setFontSizeAnchorEl(null);
  }, [keyInformationData, solutionData]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = debounceTimeouts.current;
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);
  // Log BPMN mapping changes
  // useEffect(() => {
  //   if (!keyInformationData.current_process_bpmn_mapping?.isBPMNDiagram) return;
  //   console.log("rann");

  //   setIsBpmnImported(true);
  //   setDiagramData({
  //     processedDiagramData: {
  //       diagram: {
  //         xml: keyInformationData.current_process_bpmn_mapping.xml_string,
  //         data: keyInformationData.current_process_bpmn_mapping.xml_data,
  //       },
  //     },
  //     isSaved: true,
  //     id: keyInformationData.current_process_bpmn_mapping.id,
  //     functionName: keyInformationData.current_process_bpmn_mapping.functionName,
  //     e2eName: keyInformationData.current_process_bpmn_mapping.e2eName,
  //     processName: keyInformationData.current_process_bpmn_mapping.processName,
  //     subprocessName: keyInformationData.current_process_bpmn_mapping.subprocessName,
  //   });
  // }, [keyInformationData.current_process_bpmn_mapping]);

  useEffect(() => {
    return () => {
      setIsBpmnImported(false);
      setDiagramData(null);
    };
  }, []);

  // Fetch enterprise technologies on component mount
  useEffect(() => {
    const fetchEnterpriseTechnologies = async () => {
      try {
        setLoadingTechnologies(true);
        const currentOrg = localStorage.getItem('current_organization');

        if (!currentOrg) {
          console.warn('No organization found in localStorage');
          return;
        }

        const response = await getEnterpriseTechnology(currentOrg);
        let dropdownTechnologies: string[] = [];

        // Handle new API shape with industry_data
        if (
          response &&
          response.industry_data &&
          response.industry_data.functions &&
          Array.isArray(response.industry_data.functions)
        ) {
          response.industry_data.functions.forEach((func: any) => {
            if (func.technologies && Array.isArray(func.technologies)) {
              func.technologies.forEach((tech: any) => {
                if ((tech.type === 'Core' || tech.type === 'Other') && tech.name) {
                  dropdownTechnologies.push(tech.name);
                }
              });
            }
          });
        }
        // Backward compatibility: old API
        else if (response && response.industries && Array.isArray(response.industries)) {
          response.industries.forEach((industry: any) => {
            if (industry.functions && Array.isArray(industry.functions)) {
              industry.functions.forEach((func: any) => {
                if (func.technologies && Array.isArray(func.technologies)) {
                  func.technologies.forEach((tech: any) => {
                    if ((tech.type === 'Core' || tech.type === 'Other') && tech.name) {
                      dropdownTechnologies.push(tech.name);
                    }
                  });
                }
              });
            }
          });
        }
        // Remove duplicates and sort
        const uniqueDropdownTechs = [...new Set(dropdownTechnologies)].sort();
        setEnterpriseTechnologies(uniqueDropdownTechs);
      } catch (error) {
        console.error('Error fetching enterprise technologies:', error);
        // Fallback to static vendor list if API fails
        const fallbackApps = VENDOR_LIST.map((v: { label: string }) => v.label);
        setEnterpriseTechnologies(fallbackApps);
      } finally {
        setLoadingTechnologies(false);
      }
    };

    fetchEnterpriseTechnologies();
  }, []);

  // Debounced console log function
  const handleAutoSave = useCallback(
    (data: KeyInformationData, flag?: boolean) => {
      if (currentDraftKeyRef.current !== draftKey) return;

      let updatedData = { ...data };

      if (flag === true) {
        if (generatedResponse && Object.keys(generatedResponse).length > 0) {
          updatedData.currentProcess =
            generatedResponse.current_process || updatedData.currentProcess || '';

          // Reset BPMN mapping
          updatedData.current_process_bpmn_mapping = {
            isBPMNDiagram: false,
            xml_string: '',
            xml_data: null,
            subprocessName: '',
            functionName: '',
            e2eName: '',
            processName: '',
            id: '',
          };

          updatedData.current_process_generate_process =
            generatedResponse.current_process_generate_process ||
              updatedData.current_process_generate_process || {
                url: '',
                description: '',
                name: '',
              };
        } else if (diagramData) {
          // Populate currentProcess with description from imported BPMN, or fallback to subprocess name
          updatedData.currentProcess =
            diagramData?.description ||
            diagramData?.subprocessName ||
            updatedData.currentProcess ||
            '';

          updatedData.current_process_bpmn_mapping = {
            isBPMNDiagram: true,
            xml_string: diagramData?.xml_string || '',
            xml_data: diagramData?.xml_data || null,
            functionName: diagramData?.functionName || '',
            e2eName: diagramData?.e2eName || '',
            processName: diagramData?.processName || '',
            subprocessName: diagramData?.subprocessName || '',
            id: diagramData?.id || '',
          };

          // Reset generated process info
          updatedData.current_process_generate_process = {
            url: '',
            description: '',
            name: '',
          };
        }
      } else if (flag === false) {
        updatedData.currentProcess = '';
        updatedData.current_process_bpmn_mapping = {
          isBPMNDiagram: false,
          xml_string: '',
          xml_data: null,
          subprocessName: '',
          functionName: '',
          e2eName: '',
          processName: '',
          id: '',
        };
        updatedData.current_process_generate_process = {
          url: '',
          description: '',
          name: '',
        };
      }

      latestKeyInfoRef.current = updatedData;
      handleDraftSave(updatedData);
      setKeyInformationData(updatedData);
    },
    [handleDraftSave, draftKey, diagramData, generatedResponse]
  );

  useEffect(() => {
    setDiagramData({
      isSaved: solutionData?.current_process_bpmn_mapping?.id ? true : false,
      ...solutionData?.current_process_bpmn_mapping,
    });
  }, [solutionData.id]);

  // useEffect(() => {
  //   if (diagramData?.isSaved) {
  //     handleAutoSave(keyInformationData, diagramData.isSaved ? true : false);
  //   }
  // }, [diagramData?.isSaved]);

  // Generic debounced update function
  const debouncedUpdate = useCallback(
    (
      field: keyof KeyInformationData,
      value: string | number | string[],
      immediate: boolean = false,
      userInteraction: boolean = false
    ) => {
      // Don't update in version view mode
      if (isVersionViewMode) {
        return;
      }

      debouncedUpdateField(
        field,
        value,
        debounceTimeouts,
        keyInformationRef,
        setKeyInformationData,
        userInteraction ? handleAutoSave : undefined,
        DEBOUNCE_DELAY,
        immediate
      );
    },
    [handleAutoSave, setKeyInformationData]
  );

  const handleOpenImport = () => {
    if (keyInformationData.currentProcess.length > 0) {
      setImportConfirm(true);
    } else {
      setIsImportBpmnOpen(true);
      setShowImportEditPage(false);
    }
  };

  const handleCloseImport = () => {
    setIsImportBpmnOpen(false);
    setShowImportEditPage(false);
    setSelectedFunctionId(null);
    setSelectedBpmnObj(null);
    setSelectedFiles([]);
    setImportType('import');
  };

  const handleImportBpmn = async (pocId: string, diagramId: string) => {
    try {
      didAutoSave.current = false; // reset before new import
      setGeneratedResponse({});

      const [taxonomyResponse] = await Promise.all([
        fetchSubprocessData({ variables: { filters: { id: pocId } } })
          .then((response) => response)
          .catch((error) => {
            throw error;
          }),
      ]);

      const xmlUrl = taxonomyResponse.data?.taxonomySubProcess?.get?.diagram?.xmlData;
      if (!xmlUrl) throw new Error('No diagram XML URL found in response');

      const diagramXmlResponse = await fetchDiagramData(xmlUrl, diagramId);
      if (!diagramXmlResponse || !diagramXmlResponse.xml)
        throw new Error('Failed to fetch diagram XML');

      // Extract description from the taxonomy response
      const subprocessDescription =
        taxonomyResponse.data?.taxonomySubProcess?.get?.description || '';
      const subprocessName =
        taxonomyResponse.data?.taxonomySubProcess?.get?.name ||
        selectedBpmnObj?.subprocessName ||
        '';

      setDiagramData({
        isSaved: true,
        id: selectedBpmnObj?.diagram?.id,
        functionName: selectedBpmnObj?.functionName,
        e2eName: selectedBpmnObj?.e2eName,
        processName: selectedBpmnObj?.processName,
        subprocessName: selectedBpmnObj?.subprocessName,
        subprocessId: selectedBpmnObj?.subprocessId,
        xml_string: diagramXmlResponse.xml.toString(),
        xml_data: taxonomyResponse.data?.taxonomySubProcess?.get?.diagram?.data || {},
        description: subprocessDescription || subprocessName, // Store description for currentProcess field
      });

      setIsBpmnImported(true);
      setIsImportBpmnOpen(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (!isBpmnImported || !diagramData || didAutoSave.current) return;

    const timer = setTimeout(() => {
      handleAutoSave(keyInformationData, true);
      didAutoSave.current = true;
    }, 100);

    return () => clearTimeout(timer);
  }, [isBpmnImported, diagramData]);

  // Store the latest generated response

  const uploadAndGenerate = async () => {
    try {
      setDiagramData(null);
      setIsGenerateLoading(true);

      const generateProcessUrl = ORCHESTRATOR_CONFIG.ENDPOINTS.CURRENT_PROCESS;

      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('upload', file, file.name));
      if (selectedFiles.length > 0) {
        formData.append('label', createLabelWithUsername(selectedFiles[0].name));
      }

      const res = await uploadFilesToNexus(formData);

      const files = res.files.map((file: string) => {
        return STORAGE_BASE + file;
      });

      const generatedResponse: GeneratedResponseType = await httpService(
        httpConstants.METHOD_TYPE.POST,
        generateProcessUrl,
        {
          url: files[0],
          description: additionalContent || '',
          env: ENVIRONMENT_TYPE,
          label: createLabelWithUsername(selectedFiles[0]?.name || ''),
          organization_id: selectedOrgId || '',
        },
        undefined,
        true
      );

      setIsGenerateLoading(false);

      if (!generatedResponse?.isValid) {
        ShowToast({
          message: generatedResponse?.current_process ?? 'Error While Generating',
          type: ToastTypeEnum.Error,
        });
        setSelectedFiles([]);
        setImportType('import');
        setIsImportBpmnOpen(false);
        return;
      }

      generatedResponseRef.current = generatedResponse;
      autoSaveRef.current.triggered = false;

      // Update state
      setKeyInformationData((prev: KeyInformationData) => ({
        ...prev,
        currentProcess: generatedResponse?.current_process || '',
        current_process_bpmn_mapping: {
          isBPMNDiagram: false,
          xml_string: '',
          xml_data: null,
          subprocessName: '',
          functionName: '',
          e2eName: '',
          processName: '',
          id: '',
        },
        current_process_generate_process: {
          url: files[0],
          description: additionalContent || '',
          name: selectedFiles[0]?.name || '',
        },
      }));
      setGeneratedResponse(generatedResponse);
      setImportType('import');
      setIsImportBpmnOpen(false);
      setSelectedFiles([]);
    } catch (err) {
      console.error(err);
      ShowToast({ message: 'Upload failed', type: ToastTypeEnum.Error });
      setIsGenerateLoading(false);
      setSelectedFiles([]);
    }
  };

  const autoSaveRef = useRef({ triggered: false });

  useEffect(() => {
    const response = generatedResponseRef.current;

    if (!response || autoSaveRef.current.triggered) return;

    const timer = setTimeout(() => {
      handleAutoSave(keyInformationRef.current, true);
      autoSaveRef.current.triggered = true;
    }, 200);

    return () => clearTimeout(timer);
  }, [handleAutoSave]);

  const handleEditBpmn = () => {
    setShowImportEditPage(true);
    setIsImportBpmnOpen(true);
  };

  const handleDeleteBpmn = () => {
    setIsBpmnImported(false);
    setDiagramData(null);
    setSelectedBpmnObj(null);
    setSelectedFunctionId(null);
    handleAutoSave(keyInformationData, false);
  };

  // Called when ImportEditPage updates subprocess name — refetch taxonomy and update local state
  const handleSubprocessNameUpdated = async (subprocessId?: string, newName?: string) => {
    if (!subprocessId) return;
    try {
      const taxonomyResponse = await fetchSubprocessData({
        variables: { filters: { id: subprocessId } },
      });

      const xmlUrl = taxonomyResponse.data?.taxonomySubProcess?.get?.diagram?.xmlData;
      const subprocessDescription =
        taxonomyResponse.data?.taxonomySubProcess?.get?.description || '';
      const subprocessName = taxonomyResponse.data?.taxonomySubProcess?.get?.name || newName || '';

      let xmlString = diagramData?.xml_string || '';
      let xmlData =
        taxonomyResponse.data?.taxonomySubProcess?.get?.diagram?.data ||
        diagramData?.xml_data ||
        null;

      if (xmlUrl) {
        const diagramXmlResponse = await fetchDiagramData(xmlUrl, diagramData?.id || '');
        if (diagramXmlResponse && diagramXmlResponse.xml)
          xmlString = diagramXmlResponse.xml.toString();
      }

      setDiagramData((prev: any) => ({
        ...(prev || {}),
        subprocessId,
        subprocessName,
        xml_string: xmlString,
        xml_data: xmlData,
        description: subprocessDescription || subprocessName,
        isSaved: true,
      }));

      const updatedKeyInfo = { ...keyInformationData } as KeyInformationData;
      updatedKeyInfo.currentProcess =
        subprocessDescription || subprocessName || updatedKeyInfo.currentProcess;
      updatedKeyInfo.current_process_bpmn_mapping = {
        isBPMNDiagram: true,
        xml_string: xmlString || '',
        xml_data: xmlData || null,
        functionName:
          taxonomyResponse.data?.taxonomySubProcess?.get?.functionName ||
          diagramData?.functionName ||
          '',
        e2eName:
          taxonomyResponse.data?.taxonomySubProcess?.get?.e2eName || diagramData?.e2eName || '',
        processName:
          taxonomyResponse.data?.taxonomySubProcess?.get?.processName ||
          diagramData?.processName ||
          '',
        subprocessName: subprocessName || '',
        id: taxonomyResponse.data?.taxonomySubProcess?.get?.id || diagramData?.id || '',
      } as any;

      latestKeyInfoRef.current = updatedKeyInfo;
      handleDraftSave(updatedKeyInfo);
      setKeyInformationData(updatedKeyInfo);
    } catch (err) {
      console.error('Failed to refetch subprocess after name update', err);
    }
  };

  // Utility function to check if content is effectively empty
  const isContentEmpty = (content: string): boolean => {
    if (!content) return true;

    // Remove HTML tags and check if there's any meaningful content
    const textOnly = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .trim(); // Remove whitespace

    return textOnly.length === 0;
  };

  // Enhanced content handler that normalizes empty content with debouncing
  const handleRichTextChange = useCallback(
    (field: keyof KeyInformationData) => (newContent: string) => {
      if (isVersionViewMode) {
        return;
      }

      // If user is typing and there are linked sessions, clear them
      if (userInteractionRef.current && solutionData.linkedSessions?.[field]?.length) {
        // dispatch(clearLinkedSessions({ field }));

        // Backend unlinking
        const fieldType = SOLUTION_XPLR_FIELD_MAP[field];
        if (solutionId && fieldType) {
          void deleteIdeationIntake(solutionId, fieldType);
        }
      }

      // If content is effectively empty, store as empty string
      const finalContent = isContentEmpty(newContent) ? '' : newContent;
      debouncedUpdate(field, finalContent, false, userInteractionRef.current);
    },
    [debouncedUpdate, isVersionViewMode, solutionData.linkedSessions, solutionId, dispatch]
  );

  const handleTimelineChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (isVersionViewMode) {
        return;
      }
      const value = event.target.value;
      debouncedUpdate('timeline', value, false, true);
    },
    [debouncedUpdate, isVersionViewMode]
  );

  const enterpriseAppsArray = keyInformationData.enterpriseApplications
    ? keyInformationData.enterpriseApplications
        .split(',')
        .map((app: string) => app.trim())
        .filter(Boolean)
    : [];

  const handleEnterpriseAppsChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newValue: string[]) => {
      if (isVersionViewMode) {
        return;
      }
      const joinedValue = newValue.join(', ');
      debouncedUpdate('enterpriseApplications', joinedValue, false, true);
    },
    [debouncedUpdate, isVersionViewMode]
  );

  // Use enterprise technologies from API or fallback to static list
  const predefinedApps = useMemo(() => {
    if (enterpriseTechnologies.length > 0) {
      return enterpriseTechnologies;
    }
    // Fallback to static vendor list if API data is not available
    return VENDOR_LIST.map((v: { label: string }) => v.label);
  }, [enterpriseTechnologies]);

  // Handle field expansion
  const handleFieldExpand = useCallback((fieldName: string) => {
    setExpandedField(fieldName);
  }, []);

  const handleFieldCollapse = useCallback(() => {
    setExpandedField(null);
  }, []);

  const loadSessions = async (fieldType?: string) => {
    try {
      const sessions = await getLatestQueryHistory(20, organizationId, fieldType);
      if (sessions?.data) {
        let sessionList = (sessions?.data || []).map((item: any) => ({
          id: item.id,
          use_case_title:
            item.session_name || item.ideation_intake[0]?.ideation_name || 'Untitled Session',
          session_name: item.session_name,
          created_by: item.created_by_user_name || 'admin',
          updated_by: item.updated_by_user_name || '',
          updated_at: item.updated_at,
          created_at: item.created_at,
          usedInProcess: item.field_type
            ? SOLUTION_XPLR_FIELD_MAP
              ? SOLUTION_XPLR_FIELD_MAP[item.field_type] || item.field_type
              : item.field_type
            : 'Unknown',
          conversation_id: item.conversation_id,
          version: item.version,
          ideation_intake: item.ideation_intake || [],
          field_type: item.field_type,
          semantic_knowledge: item.semantic_knowledge,
          conversation_type: item.conversation_type,
          diagram_content: item.diagram_content,
        }));

        // Apply client-side search filtering
        if (searchQuery.trim()) {
          sessionList = sessionList.filter(
            (session) =>
              session.use_case_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (session.session_name &&
                session.session_name.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        }

        setSessions(sessionList);
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    }
  };

  const SOLUTION_XPLR_FIELD_MAP: Record<string, string> = {
    opportunityStatement: 'opportunity_statement_solutionxplr',
    currentProcess: 'current_process_solutionxplr',
    proposedEnhancements: 'proposed_enhancements_solutionxplr',
    estimatedImpact: 'estimated_impact_solutionxplr',
  };

  // While previewing a version, build the Linked Object chip data from the
  // snapshot's custom_metadata.linked_ideation_intake, enriched from the live
  // linked sessions. Shared with the restore flow via buildLinkedSessionsByField.
  const versionLinkedSessionsByField = useMemo<Record<string, LinkedSession[]>>(() => {
    if (!isVersionViewMode) return {};
    return buildLinkedSessionsByField(versionLinkedIntake, solutionData.linkedSessions);
  }, [isVersionViewMode, versionLinkedIntake, solutionData.linkedSessions]);

  // // Restore linked sessions from intake API on solution load
  // const loadLinkedSessionsFromAPI = async () => {
  //   if (!useCaseId || !organizationId) return;
  //   try {
  //     const intakeResponse = await getIdeationIntakes(useCaseId, organizationId);
  //     const intakes = intakeResponse?.data || [];
  //     if (intakes.length === 0) return;

  //     // Group intakes by type
  //     const groupedByField: Record<string, any[]> = {};
  //     for (const intake of intakes) {
  //       const fieldKey = INTAKE_TYPE_TO_FIELD_KEY[intake.type];
  //       if (!fieldKey) continue;
  //       if (!groupedByField[fieldKey]) groupedByField[fieldKey] = [];
  //       groupedByField[fieldKey].push(intake);
  //     }

  //     // Dispatch linked sessions per field
  //     for (const [fieldKey, fieldIntakes] of Object.entries(groupedByField)) {
  //       const linkedSessionsToRestore: LinkedSession[] = fieldIntakes.map((intake: any) => ({
  //         id: intake.id,
  //         use_case_title: intake.session_name || 'Linked Session',
  //         session_name: intake.session_name || '',
  //         created_by: intake.created_by_name || '',
  //         created_at: intake.created_at || '',
  //         conversation_id: intake.conversation_id,
  //         field_type: intake.type,
  //         semantic_knowledge: { report: '', file_summary: '' },
  //         ideation_intake: [intake],
  //         diagram_content: intake?.diagramContent,
  //       }));
  //       dispatch(addLinkedSessions({ field: fieldKey, sessions: linkedSessionsToRestore }));
  //     }
  //   } catch (error) {
  //     console.error('Error loading linked sessions from API:', error);
  //   }
  // };

  const handleAddFromDataXplr = (label: string) => {
    console.log('......', label);

    // Remember which field opened the drawer so added knowledge is
    // associated with that field even if the user switches category in the drawer.
    setDrawerOriginField(label);
    setCategory(label);
    setDrawerOpen(true);
  };

  // Field configurations for expandable fields
  const expandableFields = useMemo(
    () => ({
      opportunityStatement: {
        title: 'Opportunity Statement',
        isRequired: true,
        placeholder: 'Describe the business opportunity that AI or native automation will unlock.',
        content: keyInformationData?.opportunityStatement || '',
        tooltip: solutionXPLRTooltipText['Opportunity Statement'],
      },
      currentProcess: {
        title: 'Current Process',
        isRequired: true,
        placeholder:
          'Describe the current process in discrete steps, applications used, and who is involved (e.g. Finance team manually enters emailed invoices into SAP for approval).',
        content: keyInformationData?.currentProcess || '',
        tooltip: solutionXPLRTooltipText['Current Process'],
      },
      proposedEnhancements: {
        title: 'Proposed Enhancements',
        isRequired: true,
        placeholder:
          'Briefly describe the improvements you propose e.g. new data source combinations, knowledge graphs, and automation that creates exponential intelligence.',
        content: keyInformationData?.proposedEnhancements || '',
        tooltip: solutionXPLRTooltipText['Proposed Enhancements'],
      },
      estimatedImpact: {
        title: 'Estimated Impact',
        isRequired: true,
        placeholder:
          'Mention the difference between current and targeted improvements at every level, along with relevant KPIs (e.g., change in volume, price, headcount, or error rate).',
        content: keyInformationData?.estimatedImpact || '',
        tooltip: solutionXPLRTooltipText['Estimated Impact'],
      },
      otherSoftware: {
        title: 'Other Software in use',
        placeholder: 'List any additional tools or software used or planned',
        content: keyInformationData?.otherSoftware || '',
        tooltip: solutionXPLRTooltipText['Other Software'],
      },
      deliveryPrefences: {
        title: 'Delivery Preferences (Format + Interface)',
        placeholder:
          'Mention the format and interface you prefer (e.g. update existing system, Chatbot or Summary Dashboard).',
        content: keyInformationData?.deliveryPrefences || '',
        tooltip: solutionXPLRTooltipText['Delivery Preferences'],
      },
    }),
    [keyInformationData]
  );

  useEffect(() => {}, [keyInformationData, uniqueKey]);

  // useEffect(() => {
  //   if (isImportBpmnOpen) {
  //     setSelectedFunctionId(functions[0]?.id);
  //   } else {
  //     setSelectedFunctionId(null);
  //   }
  // }, [functions, isImportBpmnOpen]);

  // Function to check if field has content or should always show expand button
  const shouldShowExpandButton = (): boolean => {
    // Always show expand button for better UX
    if (isVersionViewMode) {
      return false;
    }
    return true;
    // Alternative: Show only when there's some content
    // if (!content) return false;
    // const textOnly = content
    //   .replace(/<[^>]*>/g, '')
    //   .replace(/&nbsp;/g, ' ')
    //   .trim();
    // return textOnly.length > 20;
  };

  // File attachment handlers
  const maxFileSize = 5 * 1024 * 1024;
  const getAcceptAttribute = (): string => {
    return Object.keys(DEFAULT_SUPPORTED_FILE_TYPES).join(',');
  };

  const DEFAULT_SUPPORTED_FILE_TYPES = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpeg',
    'image/png': '.png',
  };

  function formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  }

  const isFileTypeSupported = (file: File): boolean => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    return (
      Object.keys(DEFAULT_SUPPORTED_FILE_TYPES).includes(file.type) ||
      (file.type === 'image/jpeg' && ['.jpg', '.jpeg'].includes(fileExtension))
    );
  };
  const DEFAULT_FILE_EXTENSIONS = ['.pdf', '.jpeg', '.png'];

  const getEffectiveFileTypes = () => {
    return DEFAULT_FILE_EXTENSIONS;
  };

  const getSupportedExtensions = (): string => {
    return getEffectiveFileTypes().join(', ');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files?.length) return;

    const freshFiles: File[] = [];

    Array.from(files).forEach((file) => {
      // Check file type
      if (!isFileTypeSupported(file)) {
        const supportedFormats = getSupportedExtensions();
        ShowToast({
          message: `${file.name} is not a supported file type. Supported formats: ${supportedFormats}`,
          type: ToastTypeEnum.Warn,
        });
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        ShowToast({
          message: `${file.name} exceeds the maximum file size of ${formatBytes(maxFileSize)}`,
          type: ToastTypeEnum.Warn,
        });
        return;
      }

      freshFiles.push(file);
    });

    if (freshFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFiles((prev) => [...prev, ...freshFiles]);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectFilesClick = () => {
    fileInputRef.current?.click();
  };

  // Maps the category label shown in the drawer to the keyInformationData field key
  const categoryToFieldKey: Record<string, keyof KeyInformationData> = {
    'Opportunity Statement': 'opportunityStatement',
    // 'Current Process (Solution)': 'currentProcess',
    'Current Process': 'currentProcess',
    // 'Current Process':            'currentProcess',
    'Proposed Enhancements': 'proposedEnhancements',
    // 'Estimated Impact (Solution)': 'estimatedImpact',
    'Estimated Impact': 'estimatedImpact',
    // 'Estimated Impact':           'estimatedImpact',
  };

  const onAdd = useCallback(
    async (selectedIds: string[]): Promise<void> => {
      if (selectedIds.length === 0) return;

      // Use the originating field (where the drawer was opened) if present,
      // otherwise fall back to the currently selected category in the drawer.
      const origin = drawerOriginField || category;
      const fieldKey = categoryToFieldKey[origin];
      if (!fieldKey) {
        console.warn('No field key mapped for category:', origin);
        setDrawerOpen(false);
        setDrawerOriginField(null);
        return;
      }

      // Get the selected sessions
      const selectedSessions = sessions.filter((s) => selectedIds.includes(s.id));

      // Map to LinkedSession for Redux
      const linkedSessionsToStore: LinkedSession[] = selectedSessions.map((s) => ({
        id: s.id,
        use_case_title: s.use_case_title,
        session_name: s.session_name,
        created_by: s.created_by,
        created_at: s.created_at,
        conversation_id: s.conversation_id,
        field_type: s.field_type,
        semantic_knowledge: s.semantic_knowledge,
        ideation_intake: s.ideation_intake,
        diagram_content: s.diagram_content,
      }));

      // Parse each session's semantic knowledge report from markdown → HTML
      const htmlReports: string[] = [];
      for (const session of selectedSessions) {
        let reportContent = '';
        if (session?.semantic_knowledge) {
          if (
            typeof session.semantic_knowledge === 'object' &&
            (session.semantic_knowledge as { report?: string }).report
          ) {
            reportContent = String(
              (session.semantic_knowledge as { report?: string }).report ?? ''
            );
          } else if (typeof session.semantic_knowledge === 'string') {
            reportContent = session.semantic_knowledge;
          }
        }

        if (reportContent) {
          const { marked } = await import('marked');
          const htmlReport = await marked.parse(reportContent);
          htmlReports.push(htmlReport);
        }
      }

      if (htmlReports.length > 0) {
        const currentValue = (keyInformationData[fieldKey] as string) || '';
        const newValue = currentValue
          ? currentValue + '<br><br>' + htmlReports.join('<br><br>')
          : htmlReports.join('<br><br>');

        // Add to linked sessions in state first so UI reflects the link immediately
        dispatch(addLinkedSessions({ field: fieldKey, sessions: linkedSessionsToStore }));

        // Immediately save the merged content into the field
        debouncedUpdate(fieldKey, newValue, true, true);
      }

      setDrawerOpen(false);
      setDrawerOriginField(null);
    },
    [category, drawerOriginField, sessions, keyInformationData, debouncedUpdate, dispatch]
  );

  const handleSearchChange = (search: string) => {
    setSearchQuery(search);
  };

  const fieldSolutionAPIKeyMap: Record<string, string> = {
    opportunityStatement: 'opportunity_statement_solutionxplr',
    currentProcess: 'current_process_solutionxplr',
    proposedEnhancements: 'proposed_enhancements_solutionxplr',
    estimatedImpact: 'estimated_impact_solutionxplr',
  };

  const getFieldTypeFromCategory = (category: string): string | undefined => {
    const categoryToFieldMap: Record<string, string> = {
      'Opportunity Statement': fieldSolutionAPIKeyMap.opportunityStatement,
      'Current Process': fieldSolutionAPIKeyMap.currentProcess,
      'Proposed Enhancements': fieldSolutionAPIKeyMap.proposedEnhancements,
      'Estimated Impact': fieldSolutionAPIKeyMap.estimatedImpact,
    };

    return categoryToFieldMap[category];
  };

  const handleCategoryChange = (selectedField: string) => {
    setCategory(selectedField);
  };

  useEffect(() => {
    // if (chatId) {
    loadSessions(getFieldTypeFromCategory(category));
    // }
  }, [category, searchQuery]);

  // Restore linked sessions from intake API on initial mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (useCaseId && organizationId) {
        try {
          const intakeResponse = await getIdeationIntakes(useCaseId, organizationId);
          if (cancelled) return;

          const intakes = intakeResponse?.data || [];
          if (intakes.length === 0) return;

          const groupedByField: Record<string, any[]> = {};
          for (const intake of intakes) {
            const fieldKey = INTAKE_TYPE_TO_FIELD_KEY[intake.type];
            if (!fieldKey) continue;
            if (!groupedByField[fieldKey]) groupedByField[fieldKey] = [];
            groupedByField[fieldKey].push(intake);
          }

          for (const [fieldKey, fieldIntakes] of Object.entries(groupedByField)) {
            const linkedSessionsToRestore: LinkedSession[] = fieldIntakes.map((intake: any) => ({
              id: intake.id,
              use_case_title: intake.session_name || 'Linked Session',
              session_name: intake.session_name || '',
              created_by: intake.created_by_name || '',
              created_at: intake.created_at || '',
              conversation_id: intake.conversation_id,
              field_type: intake.type,
              semantic_knowledge: { report: '', file_summary: '' },
              ideation_intake: [intake],
              diagram_content: intake?.diagramContent,
            }));
            dispatch(addLinkedSessions({ field: fieldKey, sessions: linkedSessionsToRestore }));
          }
        } catch (error) {
          console.error('Error loading linked sessions from API:', error);
        }
      } else if (!useCaseId) {
        dispatch(clearLinkedSessions({}));
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [useCaseId]);

  // Render expandable field component
  const renderExpandableField = (fieldKey: keyof KeyInformationData, config: any) => {
    const showExpandButton = shouldShowExpandButton();

    // const solutionXplrLabel = SOLUTION_XPLR_FIELD_MAP[fieldKey];
    // const showAddFromDataXplr = Boolean(solutionXplrLabel);

    const handleRemoveFile = () => {
      const updatedData = {
        ...keyInformationData,
        current_process_generate_process: {
          url: '',
          description: '',
          name: '',
        },
        currentProcess: '',
      };

      setKeyInformationData(updatedData);

      handleAutoSave(updatedData, false);
    };

    // In version preview mode, drive the Linked Object chip from the snapshot's
    // custom_metadata; otherwise use the live linked sessions from Redux.
    const linkedSessions = isVersionViewMode
      ? versionLinkedSessionsByField[fieldKey as string] || []
      : solutionData.linkedSessions?.[fieldKey] || [];
    const supportedLinkedFields = [
      'opportunityStatement',
      'currentProcess',
      'proposedEnhancements',
      'estimatedImpact',
      'otherSoftware',
      'deliveryPrefences',
    ];

    const dataXplrEligibleFields = [
      'opportunityStatement',
      'currentProcess',
      'proposedEnhancements',
      'estimatedImpact',
    ];

    if (supportedLinkedFields.includes(String(fieldKey))) {
      // const handleRemoveSession = (sessionId: string) => {
      //   dispatch(removeLinkedSession({ field: String(fieldKey), sessionId }));
      //   // Note: For individual removals, we might not always call backend deleteIdeationIntake
      //   // if the API only supports deleting ALL for a type, but let's check if we can do more.
      //   // Usually, removing one chip just updates state, and backend is synced via separate logic or when saving.
      // };

      return (
        <FormSection key={String(fieldKey)}>
          <SolutionMultilineField
            label={config.title}
            placeholder={isVersionViewMode ? 'No content available' : config.placeholder}
            value={config.content}
            required={config.isRequired}
            disabled={isVersionViewMode}
            onChange={(value) => {
              userInteractionRef.current = true;
              handleRichTextChange(fieldKey)(value);
            }}
            onUseDataXplrClick={
              dataXplrEligibleFields.includes(String(fieldKey))
                ? () => handleAddFromDataXplr(config.title)
                : undefined
            }
            linkedSessions={linkedSessions}
            fieldCategory={String(fieldKey)}
            extraHeaderAction={
              fieldKey === 'currentProcess' &&
              !diagramData?.isSaved &&
              !keyInformationData?.current_process_generate_process?.url?.length ? (
                <IconButton
                  onClick={handleOpenImport}
                  size="small"
                  sx={{
                    color: '#1A55F9',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    pb: 0,
                    mb: 0.7,
                    // '&:hover': { backgroundColor: '#f0f4ff' },
                  }}
                >
                  Import Process
                </IconButton>
              ) : null
            }
            solutionId={solutionId}
            orgId={selectedOrg?.id || ''}
            processStatus="SOLUTION_REQUIREMENTS"
            subProcessStatus=""
            selectedThreadId={selectedThreadId}
            setSelectedThreadId={setSelectedThreadId}
            fontSize={`text-${fontSize}`}
          />
          {fieldKey === 'currentProcess' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              {!diagramData?.isSaved &&
              !keyInformationData.current_process_generate_process?.url?.length ? (
                <Box sx={{ display: 'flex' }}>
                  {/* <IconButton
                    onClick={handleOpenImport}
                    size="small"
                    sx={{
                      color: '#1A55F9',
                      fontSize: '12px',
                      px: 1,
                      '&:hover': { backgroundColor: '#f0f4ff' },
                    }}
                  >
                    Import Process
                  </IconButton> */}
                </Box>
              ) : (
                <Box sx={{ width: '100%', minHeight: '44px', marginBottom: '16px' }}>
                  {keyInformationData?.current_process_generate_process?.url?.length ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: '4px',
                        backgroundColor: '#F3F6FF',
                        width: '100%',
                        px: '14px',
                        py: '8px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src="/images/upload-icon.svg" alt="file" />
                        <Typography
                          sx={{
                            color: '#1A55F9',
                            fontWeight: 500,
                            fontSize: 14,
                            mr: 2,
                            my: 1,
                            wordBreak: 'break-word',
                          }}
                        >
                          {`${keyInformationData?.current_process_generate_process?.name}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          sx={{ color: 'white' }}
                          disabled={isGenerateLoading}
                          onClick={() =>
                            setFilePreview(
                              keyInformationData?.current_process_generate_process?.url ||
                                URL.createObjectURL(selectedFiles[0])
                            )
                          }
                          size="small"
                          title="View File"
                        >
                          <img src="/images/eye-icon.svg" alt="view" />
                        </IconButton>
                        <IconButton
                          onClick={handleRemoveFile}
                          size="small"
                          title="Delete BPMN"
                          disabled={isGenerateLoading}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: '8px',
                        backgroundColor: '#F3F6FF',
                        px: '14px',
                        py: '8px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src="/images/import-icon.svg" alt="bpmn" />
                        <Typography
                          sx={{
                            color: '#1A55F9',
                            fontWeight: 500,
                            fontSize: 14,
                            mr: 2,
                            my: 1,
                            wordBreak: 'break-word',
                          }}
                        >
                          {`${diagramData?.e2eName || ''} / ${diagramData?.processName || ''} / ${diagramData?.subprocessName || ''}`}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton onClick={handleEditBpmn} size="small" title="Edit BPMN">
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton onClick={handleDeleteBpmn} size="small" title="Delete BPMN">
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </FormSection>
      );
    }

    return (
      <FormSection key={String(fieldKey)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', mb: 1 }}
            >
              {config.title}
              {config.isRequired && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
            </Typography>
          </Box>
        </Box>

        <ExpandableFieldContainer
          sx={{
            minHeight: '150px',
          }}
        >
          <div
            className="rich-text-editor-container"
            onClick={() => {
              userInteractionRef.current = true;
            }}
            onKeyDown={() => {
              userInteractionRef.current = true;
            }}
            onMouseDown={() => {
              userInteractionRef.current = true;
            }}
          >
            <RichTextEditor
              key={`${String(fieldKey)}-${uniqueKey}`}
              content={config.content}
              onChange={handleRichTextChange(fieldKey)}
              placeholder={isVersionViewMode ? 'No content available' : config.placeholder}
              toolbarOptions={editorOptions}
              showToolbarOnFocus={!isVersionViewMode}
              height={'228px'}
              fontSize={`text-${fontSize}`}
              readOnly={isVersionViewMode}
              solutionId={solutionId}
              orgId={selectedOrg?.id || ''}
              processStatus="SOLUTION_REQUIREMENTS"
              subProcessStatus=""
              enableComments={!isVersionViewMode}
              selectedThreadId={selectedThreadId}
              setSelectedThreadId={setSelectedThreadId}
              syncContentOnComment={() => {
                // Re-sync latest content for this field into debounced auto-save refs
                const updatedContent = keyInformationData?.[fieldKey as keyof KeyInformationData];
                if (typeof updatedContent === 'string') {
                  const updatedData = {
                    ...keyInformationData,
                    [fieldKey]: updatedContent,
                  } as KeyInformationData;
                  latestKeyInfoRef.current = updatedData;
                  handleDraftSave(updatedData);
                }
              }}
            />
          </div>
          {showExpandButton && (
            <ExpandButton
              className="expand-button"
              onClick={() => handleFieldExpand(String(fieldKey))}
              size="small"
              title="Expand to fullscreen"
            >
              <FullscreenIcon sx={{ fontSize: 16 }} />
            </ExpandButton>
          )}
        </ExpandableFieldContainer>
      </FormSection>
    );
  };

  // Render expanded view for a specific field
  const renderExpandedView = (fieldKey: string, config: any) => {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {config.title}
          </Typography>

          <IconButton
            onClick={handleFieldCollapse}
            sx={{ color: '#616161', p: 1 }}
            aria-label="Exit Fullscreen"
          >
            <CloseFullscreenOutlinedIcon />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: 2,
              fontSize: '14px',
            }}
          >
            {config.placeholder}
          </Typography>
          <Box
            sx={{
              flex: 1,
              '& .ql-editor': {
                backgroundColor: isVersionViewMode ? '#f5f5f5' : 'inherit',
                cursor: isVersionViewMode ? 'default' : 'text',
              },
              '& .ql-toolbar': {
                display: isVersionViewMode ? 'none' : 'block',
              },
            }}
          >
            <div
              className="rich-text-editor-container"
              onClick={() => {
                userInteractionRef.current = true;
              }}
              onKeyDown={() => {
                userInteractionRef.current = true;
              }}
              onMouseDown={() => {
                userInteractionRef.current = true;
              }}
            >
              <RichTextEditor
                key={`${fieldKey}-expanded-${draftKey}`}
                content={config.content}
                onChange={handleRichTextChange(fieldKey as keyof KeyInformationData)}
                //placeholder={config.placeholder}
                toolbarOptions={editorOptions}
                showToolbarOnFocus={true}
                height={'100vh'}
                maxHeight="80vh"
                fontSize={`text-${fontSize}`}
              />
            </div>
          </Box>
        </Box>
      </Box>
    );
  };

  // If a field is expanded, show only that field in fullscreen
  if (expandedField && expandableFields[expandedField as keyof typeof expandableFields]) {
    return renderExpandedView(
      expandedField,
      expandableFields[expandedField as keyof typeof expandableFields]
    );
  }

  const Content = (
    <Box
      sx={{
        width: showImportEditPage ? '100%' : isMobile ? '100%' : '80%',
        height: showImportEditPage ? '100vh' : isMobile ? '100vh' : 'auto',
        bgcolor: 'white',
        borderRadius: showImportEditPage ? 0 : isMobile ? 0 : 2,
        boxShadow: showImportEditPage ? 'none' : isMobile ? 'none' : 24,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: showImportEditPage ? 0 : isMobile ? '60px' : '45%',
        left: showImportEditPage ? 0 : isMobile ? 0 : '50%',
        transform: showImportEditPage || isMobile ? 'none' : 'translate(-50%, -50%)',
        zIndex: 1300,
        overflowY: showImportEditPage ? 'hidden' : 'auto',
      }}
    >
      {/* Header */}
      {!showImportEditPage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            height: '42px',
            borderTopLeftRadius: isMobile ? 0 : 12,
            borderTopRightRadius: isMobile ? 0 : 12,
            bgcolor: isMobile ? 'transparent' : '#1A55F9',
            color: isMobile ? 'inherit' : 'white',
            justifyContent: 'space-between',
          }}
        >
          {isMobile && (
            <Button
              onClick={handleCloseImport}
              sx={{
                minWidth: 'auto',
                mr: 1,
                p: 0,
                fontSize: 14,
              }}
            >
              <ArrowBackRounded sx={{ fontSize: 20 }} />
            </Button>
          )}

          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: isMobile ? '#000' : 'white', flex: 1, textAlign: 'left' }}
          >
            Import Process
          </Typography>

          {!isMobile && (
            <IconButton onClick={handleCloseImport} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      {/* Body */}
      {showImportEditPage ? (
        <ImportEditPage
          xml={diagramData?.xml_string}
          data={{
            diagramData: selectedBpmnObj || diagramData,
            processedData: diagramData?.xml_data,
          }}
          onClose={handleCloseImport}
          setData={(data: any) => {
            // If ImportEditPage provides a string, it's the updated XML
            if (typeof data === 'string') {
              setDiagramData((prev: any) => {
                if (!prev) return prev;
                return { ...prev, xml_string: data, isSaved: true };
              });
              handleAutoSave(keyInformationData, true);
              return;
            }

            // If ImportEditPage provides an object with diagramData, merge metadata
            if (data && data.diagramData) {
              setDiagramData((prev: any) => {
                const base = prev || {};
                return { ...base, ...data.diagramData, isSaved: true };
              });

              // update currentProcess and mapping so parent form reflects the new name
              const updatedKeyInfo = { ...keyInformationData };
              updatedKeyInfo.currentProcess =
                data.diagramData?.description || data.diagramData?.subprocessName || '';
              updatedKeyInfo.current_process_bpmn_mapping = {
                isBPMNDiagram: true,
                xml_string: data.diagramData?.xml_string || '',
                xml_data: data.diagramData?.xml_data || null,
                functionName: data.diagramData?.functionName || '',
                e2eName: data.diagramData?.e2eName || '',
                processName: data.diagramData?.processName || '',
                subprocessName: data.diagramData?.subprocessName || '',
                id: data.diagramData?.id || '',
              } as any;

              // Persist draft and update UI
              latestKeyInfoRef.current = updatedKeyInfo;
              handleDraftSave(updatedKeyInfo);
              setKeyInformationData(updatedKeyInfo);
              return;
            }
          }}
          diagramId={diagramData?.id}
          subprocessId={selectedBpmnObj?.subprocessId || diagramData?.subprocessId}
          onNameUpdated={handleSubprocessNameUpdated}
        />
      ) : taxonomyLoading ? (
        <Grid
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {/* <Loader message="Loading Data" fullScreen={false} /> */}
        </Grid>
      ) : (
        <Grid>
          {/* Modal Body */}
          <Box sx={{ p: 3, flex: 1, overflowY: 'auto', height: '540px' }}>
            <Box
              sx={{
                borderBottom: '1px solid #DDE1E6',
                height: '23px',
                marginBottom: '17px',
                display: 'flex',
                gap: 4,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontSize: '15px',
                  color: importType === 'import' ? '#2653F8' : '#000000',
                  borderBottom: importType === 'import' ? '2px solid #2653F8' : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setImportType('import')}
              >
                Select Process{' '}
              </Typography>
              {/* <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontSize: '15px',
                  color: importType === 'upload' ? '#2653F8' : '#000000',
                  borderBottom: importType === 'upload' ? '2px solid #2653F8' : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setImportType('upload')}
              >
                Upload Process{' '}
              </Typography> */}
            </Box>
            {importType === 'import' ? (
              <Box>
                <Typography variant="body2" mb={1} sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Select a function
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={selectedFunctionId ?? ''}
                  onChange={(e) => setSelectedFunctionId(e.target.value)}
                >
                  {functions.map((fn) => (
                    <MenuItem key={fn.id} value={fn.id}>
                      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500 }}>
                        {fn.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {/* Search */}
                <TextField
                  placeholder="Search"
                  fullWidth
                  size="small"
                  sx={{ mt: 2, fontSize: '15px' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {/* BPMN List */}
                <Typography
                  variant="body2"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 600, fontSize: '16px' }}
                >
                  {filteredList.length} Process found
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #eee',
                    borderRadius: 1,
                    height: '100%',
                    maxHeight: '300px',
                    overflow: 'auto',
                  }}
                >
                  {filteredList.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        px: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid #DDE1E6',
                        height: isMobile ? '80px' : '50px',
                        '&:hover': { bgcolor: '#f5f7ff' },
                        ...(selectedBpmnObj?.subprocessId === item.subprocessId && {
                          bgcolor: '#f0f4ff',
                        }),
                      }}
                      onClick={() => handleSelectBpmn(item)}
                    >
                      {!isMobile ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ color: '#737373' }} fontSize={14}>
                            {`${item?.e2eName}/${item?.processName}/ `}
                          </Typography>
                          <Typography fontSize={14}>{` ${item?.subprocessName}`}</Typography>
                        </Box>
                      ) : (
                        <Typography fontSize={14}>
                          {item?.e2eName}/{item?.processName}/
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {item?.subprocessName}
                          </Box>
                        </Typography>
                      )}
                      {selectedBpmnObj?.subprocessId === item.subprocessId && (
                        <img
                          src="/images/selected-process.svg"
                          alt="Selected"
                          width={16}
                          height={16}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 500 }}>
                  Upload file/files containing the process flow
                </Typography>
                {selectedFiles.length > 0 ? (
                  <Box
                    sx={{
                      height: '44px',
                      background: '#F3F6FF',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: '14px',
                    }}
                  >
                    <Typography sx={{ fontSize: '15px', color: '#1A55F9', fontWeight: 500 }}>
                      {selectedFiles[0]?.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        sx={{ color: 'white' }}
                        disabled={isGenerateLoading}
                        onClick={() =>
                          setFilePreview(
                            keyInformationData?.current_process_generate_process?.url ||
                              URL.createObjectURL(selectedFiles[0])
                          )
                        }
                      >
                        <img src="/images/eye-icon.svg" alt="view" />
                      </IconButton>
                      <IconButton
                        onClick={() => setSelectedFiles([])}
                        size="small"
                        title="Delete BPMN"
                        disabled={isGenerateLoading}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '150px',
                      borderRadius: '6px',
                      border: '1px dashed #D4DADF',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={handleSelectFilesClick}
                  >
                    <img
                      src="/images/upload-process-icon.svg"
                      alt="upload"
                      width={46}
                      height={46}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept={getAcceptAttribute()}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <Typography sx={{ fontSize: 14, color: '#8D9499', mt: 1, mr: '4px' }}>
                        Drag and drop or
                      </Typography>
                      <Typography
                        sx={{
                          color: '#1A55F9',
                          fontSize: 14,
                          mt: 1,
                        }}
                      >
                        Browse
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, color: '#A2A2A2', mt: 1 }}>
                      Supported formats: PDF, PNG and JPEG
                    </Typography>
                  </Box>
                )}
                {/* Additional Notes and Loading*/}
                {isGenerateLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      border: '1px solid #D4DADF',
                      borderRadius: '6px',
                      height: '150px',
                    }}
                  >
                    <img
                      src="/images/rotating-icon.svg"
                      alt="loading"
                      width={40}
                      height={40}
                      className="rotating-img"
                    />
                    <Typography sx={{ fontSize: '15px', color: '#ABABAB', mt: 1, fontWeight: 500 }}>
                      Generating process description
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 500 }}>
                      Additional Process Notes (Optional)
                    </Typography>
                    <textarea
                      style={{
                        fontSize: '15px',
                        borderRadius: '6px',
                        outline: 'none',
                        height: '150px',
                        resize: 'none',
                        border: '1px solid #D4DADF',
                        fontFamily: 'Poppins',
                        padding: '8px',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                      placeholder="Add details about your current process which are not captured in process flow"
                      value={additionalContent}
                      onChange={(e) => setAdditionalContent(e.target.value)}
                    ></textarea>
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              px: 3,
              py: 2,
              borderTop: '1px solid #eee',
            }}
          >
            <Button variant="text" onClick={handleCloseImport}>
              Close
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: '#1A55F9' }}
              onClick={() => {
                if (importType === 'import') {
                  handleImportBpmn(
                    selectedBpmnObj?.subprocessId ?? selectedBpmnObj?.fullObject?.id ?? '',
                    selectedBpmnObj?.fullObject?.diagram?.id ?? ''
                  );
                } else {
                  uploadAndGenerate();
                }
              }}
              disabled={
                importType === 'import'
                  ? !selectedBpmnObj
                  : selectedFiles.length === 0 || isGenerateLoading
              }
            >
              {importType === 'import' ? 'Import' : 'Generate Process'}
            </Button>
          </Box>
        </Grid>
      )}
    </Box>
  );

  return (
    <StyledContainer disableGutters>
      {/* Sticky Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'white',
          py: 2,
          px: 0.3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Provide general information related to your use case
        </Typography>
        {!isVersionViewMode && !isMobile && (
          <Button
            variant="text"
            size="small"
            onClick={handleFontSizeClick}
            sx={{
              textTransform: 'none',
              padding: '4px 8px',
              fontFamily: 'Poppins',
              fontSize: '16px',
              color: '#000000',
              display: 'flex',
              gap: '4px',
              minWidth: 'auto',
              height: '32px',
              border: '1px solid #000000',
              '&:hover': {
                backgroundColor: '#D6E9FF',
              },
            }}
          >
            <Box
              component="img"
              src="/images/fontsize.svg"
              alt="Font Size"
              sx={{
                width: 'full',
                height: 'full',
                marginRight: '4px',
                padding: '2px',
              }}
            />
          </Button>
        )}

        {/* Font Size Menu */}
        <Menu
          anchorEl={fontSizeAnchorEl}
          open={fontSizeMenuOpen}
          onClose={handleFontSizeClose}
          disableScrollLock
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: '10px',
                border: '1px solid #DDDFDE',
                minWidth: '120px',
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  fontFamily: 'Poppins',
                  padding: '8px 16px',
                },
              },
              elevation: 1,
            },
          }}
        >
          <MenuItem
            onClick={() => handleFontSizeSelect('small')}
            selected={fontSize === 'small'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={
                fontSize === 'small'
                  ? '/images/radio-button-active.svg'
                  : '/images/radio-button.svg'
              }
              alt="small-font-indicator"
              sx={{
                width: 16,
                height: 16,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />

            <Typography fontSize={14}>Small</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => handleFontSizeSelect('medium')}
            selected={fontSize === 'medium'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={
                fontSize === 'medium'
                  ? '/images/radio-button-active.svg'
                  : '/images/radio-button.svg'
              }
              alt="small-font-indicator"
              sx={{
                width: 16,
                height: 16,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            Medium
          </MenuItem>
          <MenuItem
            onClick={() => handleFontSizeSelect('large')}
            selected={fontSize === 'large'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={
                fontSize === 'large'
                  ? '/images/radio-button-active.svg'
                  : '/images/radio-button.svg'
              }
              alt="small-font-indicator"
              sx={{
                width: 16,
                height: 16,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            Large
          </MenuItem>
        </Menu>
      </Box>
      <Box
        sx={{
          overflowY: 'auto', //  moved scroll behavior to wrapper
          pb: 2,
          height: 'calc(100vh - 80px)', // ensures scroll area starts below header height (~80px)
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: '1px',
        }}
      >
        <Grid container spacing={2}>
          {/* Scrollable Content */}
          <Grid
            size={{ xs: 12, md: evaluateObject?.isOpen ? 8 : 12 }}
            sx={{
              flex: 1,
              overflowY: 'auto',
              pb: 2,

              // Hide scrollbar but keep functionality
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              // Firefox
              scrollbarWidth: 'none',
              // IE and Edge
              '-ms-overflow-style': 'none',
            }}
          >
            {/* Render fields in the correct order as per Figma */}
            {/* 1. Opportunity Statement */}
            {renderExpandableField('opportunityStatement', expandableFields.opportunityStatement)}

            {/* 2. Current Process */}
            {renderExpandableField('currentProcess', expandableFields.currentProcess)}

            {/* 3. Proposed Enhancements */}
            {renderExpandableField('proposedEnhancements', expandableFields.proposedEnhancements)}

            {/* 4. Estimated Impact */}
            {renderExpandableField('estimatedImpact', expandableFields.estimatedImpact)}

            {/* 5. Enterprise Applications in Use - Now positioned after Estimated Impact */}
            {/* 5. Enterprise Applications in Use - Now positioned after Estimated Impact */}
            <FormSection>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  color: isVersionViewMode ? '#999' : 'inherit',
                  mb: 1,
                }}
              >
                Enterprise Applications in Use{' '}
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              </Typography>

              <Box
                sx={{
                  opacity: isVersionViewMode ? 0.7 : 1,
                  pointerEvents: isVersionViewMode ? 'none' : 'auto',
                }}
              >
                <Autocomplete
                  key={`enterprise-apps-${uniqueKey}`}
                  multiple
                  freeSolo
                  options={predefinedApps}
                  value={enterpriseAppsArray}
                  onChange={handleEnterpriseAppsChange}
                  loading={loadingTechnologies}
                  loadingText="Loading enterprise technologies..."
                  noOptionsText={loadingTechnologies ? 'Loading...' : 'No core technologies found'}
                  disabled={isVersionViewMode}
                  renderTags={(value: string[], getTagProps) =>
                    value?.map((option: string, index: number) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={
                          <CloseIcon
                            sx={{
                              fontSize: 12,
                              color: 'primary.main',
                              transition: 'color 0.2s',
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: '#f0f4ff',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: '#1A55F9',
                          color: 'primary.main',
                          fontWeight: 400,
                          fontSize: 14,
                          height: 28,
                          '& .MuiChip-deleteIcon': {
                            marginRight: '4px',
                            color: 'primary.main',
                            fontSize: 16,
                            display: isVersionViewMode ? 'none' : 'block',
                          },
                          '& .MuiChip-deleteIcon:hover': {
                            color: 'error.main',
                            backgroundColor: 'transparent',
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        loadingTechnologies
                          ? 'Loading enterprise technologies...'
                          : isVersionViewMode
                            ? ''
                            : 'Mention the core systems currently used in the process'
                      }
                      sx={{
                        '& .MuiInputBase-input::placeholder': {
                          color: '#8d9499',
                          opacity: 1,
                        },
                        fontSize: 16,
                        fontFamily: 'Poppins, sans-serif',
                        backgroundColor: isVersionViewMode ? '#f5f5f5' : 'white',

                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderRadius: '6px',
                            borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                            borderWidth: 1,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0',
                            borderRadius: '6px',
                          },
                          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                          },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                            borderWidth: 1,
                          },
                          // Focused
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                            borderWidth: 1,
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    backgroundColor: isVersionViewMode ? '#f5f5f5' : 'white',
                    '& .MuiOutlinedInput-root': {
                      paddingY: '4px',
                    },
                  }}
                />
              </Box>
            </FormSection>

            {/* 6. Other Softwares in use */}
            {renderExpandableField('otherSoftware', expandableFields.otherSoftware)}

            {/* 7. Delivery Preferences */}
            {renderExpandableField('deliveryPrefences', expandableFields.deliveryPrefences)}

            {/* 8. Timeline - Now at the end */}
            <FormSection>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    color: isVersionViewMode ? '#999' : 'inherit',
                  }}
                >
                  Timeline
                </Typography>
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              </Box>

              <FormControl fullWidth>
                <Select
                  key={`timeline-${uniqueKey}`}
                  value={keyInformationData.timeline || ''}
                  onChange={handleTimelineChange}
                  displayEmpty
                  disabled={isVersionViewMode}
                  renderValue={(selected: string) =>
                    selected === '' ? (
                      <span style={{ color: '#8d9499' }}>
                        {isVersionViewMode ? '' : '3 months / 6 months / 12 months'}
                      </span>
                    ) : (
                      selected
                    )
                  }
                  sx={{
                    backgroundColor: isVersionViewMode ? '#f5f5f5' : 'white',
                    fontSize: 16,
                    opacity: isVersionViewMode ? 0.7 : 1,
                    '& .MuiSelect-select': {
                      paddingY: '8px',
                      borderColor: '#1A55F9',
                      '&:hover fieldset': {
                        borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0',
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                      borderWidth: 1,
                    },
                    // Focused
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: isVersionViewMode ? '#e0e0e0' : '#1A55F9',
                      borderWidth: 1,
                    },
                  }}
                >
                  <MenuItem value="1-3 Months">1–3 Months</MenuItem>
                  <MenuItem value="3-6 Months">3–6 Months</MenuItem>
                  <MenuItem value="6-12 Months">6–12 Months</MenuItem>
                  <MenuItem value="12+ Months">12+ Months</MenuItem>
                </Select>
              </FormControl>
            </FormSection>
          </Grid>

          {/* Preview Panel */}
          {filePreview && (
            <FileViewer
              fileUrl={filePreview}
              onClose={() => setFilePreview(null)}
              fileName={
                keyInformationData?.current_process_generate_process?.name || selectedFiles[0]?.name
              }
            />
          )}

          {evaluateObject?.isOpen && (
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                display: 'block',
              }}
            >
              <EvaluationReportV2
                isOpen={evaluateObject.isOpen}
                onClose={handleCloseEvaluate}
                loading={evaluateObject.loading}
                ReportObject={evaluateObject.data}
                setReportObject={() => {}}
                isMinimized={true}
                isFitToContent={true}
                showQuestions={false}
                requiredChipFields={['Current Process', 'Estimated Impact']}
                subtitle={'Recommendations to help you improve your solution details form'}
                fromSolutionXPLR={true}
                evaluateSuccess={evaluateSuccess}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Import BPMN Modal */}
      {importConfirm ? (
        <ConfirmationModal
          isOpen={importConfirm}
          title="Confirm BPMN Import"
          description={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#4f4f4f' }}>
                Importing a Process will overwrite your text input. You can provide either text or
                import Process, not both.
              </Typography>
            </Box>
          }
          primaryButtonText={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Proceed</Typography>
            </Box>
          }
          secondaryButtonText="Cancel"
          onPrimaryAction={() => {
            setIsImportBpmnOpen(true);
            setImportConfirm(false);
          }}
          onClose={() => setImportConfirm(false)}
          onSecondaryAction={() => setImportConfirm(false)}
          width="600px"
          outerPadding={0}
        />
      ) : (
        <>
          {isMobile ? (
            isImportBpmnOpen && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 50,
                  left: 0,
                  width: '100%',
                  height: '100vh',
                  bgcolor: 'white',
                  zIndex: 1300,
                  overflowY: 'auto',
                }}
              >
                {Content}
              </Box>
            )
          ) : (
            <Modal open={isImportBpmnOpen} onClose={handleCloseImport}>
              {Content}
            </Modal>
          )}
        </>
      )}

      <RightChatDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerOriginField(null);
        }}
        sessions={sessions}
        onAdd={onAdd}
        onCategoryChange={handleCategoryChange}
        onSearchChange={handleSearchChange}
        onRefetch={() => loadSessions(getFieldTypeFromCategory(category))}
        useCaseId={useCaseId}
        currentFieldLabel={drawerOriginField || category}
        isSolutionXPLR={true}
      />
    </StyledContainer>
  );
};

export const FileViewer: React.FC<FileViewerProps> = ({ fileUrl, onClose, fileName }) => {
  const [error, setError] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef<boolean>(true);
  const [zoom, setZoom] = useState(1);

  const checkFileType = async (
    file: Blob | string | null | undefined
  ): Promise<string | Blob | undefined> => {
    if (!file) return undefined;
    if (file instanceof Blob) return file;
    if (typeof file === 'string' && file.startsWith('blob:')) return file;
    if (typeof file === 'string') {
      try {
        const blob = await storageService.getFileBlob(file);
        return URL.createObjectURL(blob);
      } catch (err) {
        console.error('File fetch failed:', err);
        return storageService.getProxyUrl(file);
      }
    }
    return undefined;
  };

  // AFTER — checks fileName first, falls back to the raw fileUrl
  const isImageFile = (url: string | undefined) => {
    if (fileName && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName)) return true;
    if (typeof fileUrl === 'string' && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileUrl)) return true;
    if (url && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url)) return true;

    return false;
  };
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 4));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.25));
  const handleZoomReset = () => setZoom(1);

  useEffect(() => {
    setIsLoading(true);
    isLoadingRef.current = true;
    setProcessedUrl(undefined);
    setError(null);
    setZoom(1);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    async function loadFile() {
      try {
        const url = await checkFileType(fileUrl);
        if (url && typeof url === 'string') {
          setProcessedUrl(url);
          setError(null);
          setIsLoading(false);
          isLoadingRef.current = false;
        } else {
          setError('Invalid URL, please try again later');
          setProcessedUrl(undefined);
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      } catch (err) {
        setError('Failed to load file');
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    }

    loadFile();

    // Set a timeout to detect if iframe fails to load
    timeoutRef.current = setTimeout(() => {
      // If still loading after 10 seconds, consider it an error
      if (isLoadingRef.current) {
        setError('Invalid URL, please try again later');
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    }, 10000); // 10 second timeout

    // Cleanup timeout on unmount or fileUrl change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [fileUrl]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    isLoadingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleIframeError = () => {
    setError('Invalid URL, please try again later');
    setIsLoading(false);
    isLoadingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const isImage = isImageFile(processedUrl);
  // Zoom controls — only shown for images
  const ZoomControls = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mr: 1,
      }}
    >
      <IconButton
        onClick={handleZoomOut}
        disabled={zoom <= 0.25}
        size="small"
        title="Zoom Out"
        sx={{ color: '#444' }}
      >
        <RemoveCircleOutline fontSize="small" />
      </IconButton>

      <Button
        onClick={handleZoomReset}
        size="small"
        variant="outlined"
        sx={{
          minWidth: '54px',
          fontSize: '12px',
          px: 0.5,
          py: 0.25,
          borderColor: '#ccc',
          color: '#444',
          '&:hover': { borderColor: '#999' },
        }}
      >
        {Math.round(zoom * 100)}%
      </Button>

      <IconButton
        onClick={handleZoomIn}
        disabled={zoom >= 4}
        size="small"
        title="Zoom In"
        sx={{ color: '#444' }}
      >
        <AddCircleOutline fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: ' rgba(0, 0, 0, 0.5)',
        zIndex: 2500,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          width: '80%',
          height: '80%',
          boxShadow: 3,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: 'white',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              p: 2,
              fontSize: '15px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '60%',
            }}
          >
            {fileName || 'Document Preview'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Show zoom controls only for images */}
            {isImage && !isLoading && !error && <ZoomControls />}

            <Button variant="contained" onClick={() => onClose()} sx={{ mr: 1.5 }}>
              Close
            </Button>
          </Box>
        </Box>

        {/* Content area */}
        {error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              p: 4,
            }}
          >
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 500,
                color: '#000000',
                textAlign: 'center',
                mb: 2,
              }}
            >
              {error}
            </Typography>
          </Box>
        ) : processedUrl ? (
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                  textAlign: 'center',
                }}
              >
                <CircularProgress size={40} />
                <Typography sx={{ fontSize: '14px', color: '#666', mt: 1 }}>
                  Loading Preview...
                </Typography>
              </Box>
            )}

            {isImage ? (
              // ── IMAGE viewer with pan + zoom ──────────────────────────
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                  display: 'flex',
                  justifyContent: zoom <= 1 ? 'center' : 'flex-start',
                  alignItems: zoom <= 1 ? 'center' : 'flex-start',
                  backgroundColor: '#f5f5f5',
                  cursor: zoom > 1 ? 'grab' : 'default',
                }}
              >
                <img
                  src={processedUrl}
                  alt={fileName || 'Preview'}
                  onLoad={() => {
                    setIsLoading(false);
                    isLoadingRef.current = false;
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                  }}
                  onError={() => {
                    setError('Failed to load image');
                    setIsLoading(false);
                    isLoadingRef.current = false;
                  }}
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: zoom <= 1 ? 'center center' : 'top left',
                    transition: 'transform 0.15s ease',
                    maxWidth: zoom <= 1 ? '100%' : 'none',
                    maxHeight: zoom <= 1 ? '100%' : 'none',
                    objectFit: 'contain',
                    display: 'block',
                    margin: zoom <= 1 ? 'auto' : '16px',
                  }}
                />
              </Box>
            ) : (
              // ── PDF / other file viewer (iframe) ──────────────────────
              <iframe
                ref={iframeRef}
                src={`${processedUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  backgroundColor: 'white',
                  display: 'block',
                }}
                title="Document Preview"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            )}
          </Box>
        ) : isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <CircularProgress size={60} />
            <Typography sx={{ fontSize: '16px', color: '#666', mt: 2 }}>
              Preparing document...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              p: 4,
            }}
          >
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 500,
                color: '#000000',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Invalid URL or file not found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default KeyInformation;
